import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, FleetStatusBadge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, EmptyState, Spinner, Stat } from '../../components/Card';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useIsDark } from '../../hooks/useTheme';
import { useAuthStore } from '../../app/authStore';
import { formatNumber } from '@shared/format';
import { useFleetLinks, useLiveTick, useTrailSession } from '../../drone/hooks';
import { getTrail, useTrailStore } from '../../drone/trail';
import { useFleetStore } from './fleetStore';
import { droneFleetStatus } from './status';
import { DroneCard } from './components/DroneCard';
import type { FleetMarker } from './components/FleetMap';
import { FleetMap } from './components/FleetMap';

/** 地图在全屏高度里的占比：桌面端更高，移动端压缩到一半便于看到卡片 */
const MAP_HEIGHT_CLASS = 'h-[46vh] min-h-[260px] lg:h-[52vh] lg:min-h-[360px]';

export function OverviewPage() {
  const navigate = useNavigate();
  const { drones, groups, groupUsage, loading, loaded, error, load } = useFleetStore();
  const canStream = useAuthStore((state) => state.permissions.includes('fleet.telemetry.stream'));
  const entries = useFleetLinks(drones);
  // 离开本页即丢弃尾迹，重新进入时从当前机位重新累积
  useTrailSession();
  const trailVersion = useTrailStore((state) => state.version);
  const now = useLiveTick(1_000);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { isMobile } = useBreakpoint();
  const dark = useIsDark();

  useEffect(() => {
    void load();
  }, [load]);

  const statusById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof droneFleetStatus>>();
    for (const drone of drones) map.set(drone.id, droneFleetStatus(drone, entries[drone.id], now));
    return map;
  }, [drones, entries, now]);

  const summary = useMemo(() => {
    let online = 0;
    let flying = 0;
    let faulty = 0;
    let batterySum = 0;
    let batteryCount = 0;
    let qualitySum = 0;
    let qualityCount = 0;

    for (const drone of drones) {
      const status = statusById.get(drone.id);
      if (status === 'disabled' || status === 'offline') continue;
      online += 1;
      if (status === 'flying') flying += 1;
      if (status === 'fault') faulty += 1;

      const telemetry = entries[drone.id]?.telemetry;
      if (!telemetry) continue;
      batterySum += telemetry.battery.percent;
      batteryCount += 1;
      qualitySum += telemetry.link.quality;
      qualityCount += 1;
    }

    return {
      total: drones.length,
      online,
      flying,
      faulty,
      offline: drones.length - online,
      avgBattery: batteryCount === 0 ? null : batterySum / batteryCount,
      avgQuality: qualityCount === 0 ? null : qualitySum / qualityCount,
    };
  }, [drones, statusById, entries]);

  const markers = useMemo<FleetMarker[]>(() => {
    void trailVersion; // 航迹更新时重算（轨迹是可变数组，靠版本号驱动）
    return drones
      .map((drone) => {
        const telemetry = entries[drone.id]?.telemetry;
        if (!telemetry) return null;
        return {
          id: drone.id,
          lng: telemetry.position.lng,
          lat: telemetry.position.lat,
          headingDeg: telemetry.flight.armed ? telemetry.attitude.yawDeg : null,
          status: statusById.get(drone.id) ?? 'offline',
          label: drone.name || drone.uniqueId,
          trail: getTrail(drone.id),
        } satisfies FleetMarker;
      })
      .filter((marker): marker is FleetMarker => marker !== null);
  }, [drones, entries, statusById, trailVersion]);

  const selectedDrone = selectedId ? drones.find((drone) => drone.id === selectedId) ?? null : null;

  function operate(droneId: string) {
    navigate(`/fleet/tracking/${droneId}`);
  }

  if (!loaded && loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card tone="critical">
          <CardBody>
            <EmptyState
              title="机队数据加载失败"
              description={error}
              action={
                <Button size="sm" onClick={() => void load({ force: true })}>
                  重试
                </Button>
              }
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="flex flex-col gap-3 p-3 lg:p-4">
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-6">
          <KpiCard label="在册无人机" value={formatNumber(summary.total)} hint="已注册设备总数" />
          <KpiCard
            label="在线"
            value={formatNumber(summary.online)}
            tone={summary.online > 0 ? 'ok' : 'neutral'}
            hint="近阈值内收到遥测帧"
          />
          <KpiCard label="飞行中" value={formatNumber(summary.flying)} tone={summary.flying > 0 ? 'info' : 'neutral'} />
          <KpiCard
            label="告警"
            value={formatNumber(summary.faulty)}
            tone={summary.faulty > 0 ? 'critical' : 'neutral'}
            hint="存在严重告警的无人机数量"
          />
          <KpiCard
            label="平均电量"
            value={summary.avgBattery === null ? '--' : `${formatNumber(summary.avgBattery, 0)}%`}
          />
          <KpiCard
            label="平均信号"
            value={summary.avgQuality === null ? '--' : `${formatNumber(summary.avgQuality, 0)}%`}
          />
        </div>

        <Card className="overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
            <span className="text-sm font-semibold">机队总览地图</span>
            <span className="text-[11px] text-fg-subtle">
              位置与航迹均来自无人机实时接口，平台不存储历史轨迹
            </span>
            <div className="ml-auto flex items-center gap-1.5">
              <Badge tone="ok" solid>
                在线 {summary.online}
              </Badge>
              <Badge tone="neutral">离线 {summary.offline}</Badge>
              {!canStream ? <Badge tone="info">当前角色仅可查看</Badge> : null}
            </div>
          </div>

          {drones.length === 0 ? (
            <CardBody>
              <EmptyState
                title="还没有注册任何无人机"
                description="先在「无人机管理」里登记设备接入信息，或一键导入本地 Mock 机队。"
                action={
                  <Link
                    to="/fleet/drones"
                    className="text-sm font-medium text-accent hover:underline"
                  >
                    前往无人机管理
                  </Link>
                }
              />
            </CardBody>
          ) : (
            <FleetMap
              markers={markers}
              selectedId={selectedId}
              onSelect={(id) => setSelectedId((current) => (current === id ? null : id))}
              className={`border-b border-line ${MAP_HEIGHT_CLASS}`}
            />
          )}

          {selectedDrone ? (
            <div className="flex flex-wrap items-center gap-2 bg-surface-2 px-3 py-2.5">
              <span className="text-xs text-fg-muted">已选择</span>
              <span className="text-sm font-semibold">{selectedDrone.name || selectedDrone.uniqueId}</span>
              <Badge tone="neutral">{selectedDrone.uniqueId}</Badge>
              {selectedDrone.enabled ? null : <Badge tone="caution">已停用</Badge>}
              <div className="ml-auto flex items-center gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => setSelectedId(null)}>
                  取消选择
                </Button>
                <Button size="sm" variant="primary" onClick={() => operate(selectedDrone.id)}>
                  进入单机管理
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-surface-2 px-3 py-2.5 text-xs text-fg-subtle">
              {isMobile ? '点按卡片或地图标记选择无人机' : '单击卡片或地图标记选择无人机，双击卡片直接进入单机管理'}（不支持多选）
            </div>
          )}
        </Card>

        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">无人机卡片</h2>
          <span className="text-[11px] text-fg-subtle">共 {drones.length} 台</span>
          {Object.keys(groupUsage).length > 0 ? (
            <div className="ml-auto flex flex-wrap gap-1.5">
              {groups.map((group) => (
                <Badge key={group.id} tone="neutral">
                  {group.name} {groupUsage[group.id] ?? 0}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>

        {drones.length === 0 ? (
          <Card>
            <CardBody>
              <EmptyState title="暂无设备卡片" description="注册无人机后这里会显示每台设备的关键指标。" />
            </CardBody>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {drones.map((drone) => {
              const group = groups.find((item) => item.id === drone.groupId);
              return (
                <DroneCard
                  key={drone.id}
                  drone={drone}
                  entry={entries[drone.id]}
                  now={now}
                  selected={drone.id === selectedId}
                  groupName={group?.name ?? '未分组'}
                  groupColor={group?.color ?? null}
                  onSelect={() => setSelectedId((current) => (current === drone.id ? null : drone.id))}
                  onOperate={() => operate(drone.id)}
                />
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pb-1 text-[11px] text-fg-subtle">
          <span>地图底图 © 高德地图</span>
          <span>·</span>
          <span>深色底图 {dark ? '已启用' : '未启用'}</span>
          <span>·</span>
          <span>状态颜色：</span>
          <FleetStatusBadge status="flying" />
          <FleetStatusBadge status="idle" />
          <FleetStatusBadge status="offline" />
          <FleetStatusBadge status="fault" />
          <FleetStatusBadge status="disabled" />
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'ok' | 'info' | 'critical';
}) {
  return (
    <Card>
      <CardBody className="py-2.5">
        <Stat label={label} value={value} tone={tone} size="md" hint={hint} />
      </CardBody>
    </Card>
  );
}
